import type { ReactNode } from "react";
import type {
  FpLinesFilter,
  PublicQuoteLine,
  PublicQuoteSectionLine,
} from "@forgepoint/shared";
import { buildItemNumbers } from "./lineFields";
import { LineScopeContext, useLineScope } from "./lineScopeContext";
import { useQuoteDocumentContext } from "./quoteDocumentContext";
import { FIELD_PLACEHOLDER_CHIP_CLASS } from "./quoteFields";

interface FpLinesProps {
  /** The line-type filter from the `filter` attribute. */
  filter: FpLinesFilter;
  /** Line custom-field name driving item numbers, from `number-source`. */
  numberFieldName: string | null;
  /** Stamps the template body once, keyed by the given stamp key, or null when the template child is missing. */
  stampTemplate: ((stampKey: string) => ReactNode[]) | null;
}

/**
 * Renders `<fp-lines>`: stamps its template body once per matching quote line,
 * scoped to the enclosing section under section grouping.
 * @param props - The line filter, number source, and template stamp
 * @returns The stamped line instances, or a placeholder chip without a template
 */
export function FpLines({
  filter,
  numberFieldName,
  stampTemplate,
}: FpLinesProps) {
  const { quote } = useQuoteDocumentContext();
  const scope = useLineScope();
  if (!stampTemplate) {
    return (
      <span className={`${FIELD_PLACEHOLDER_CHIP_CLASS} font-mono`}>
        {"⟨fp-lines: missing template⟩"}
      </span>
    );
  }
  const baseLines = scope?.section
    ? sectionMemberLines(quote.lines, scope.section)
    : quote.lines;
  const lines = filterLines(baseLines, filter);
  const itemNumbers = buildItemNumbers(quote, numberFieldName);
  return (
    <>
      {lines.map((line) => (
        <LineScopeContext.Provider
          key={line.id}
          value={{
            line,
            itemNumber: itemNumbers.get(line.id) ?? null,
            section: line.type === "SECTION" ? line : (scope?.section ?? null),
            quantityBreak: null,
          }}
        >
          {stampTemplate(line.id)}
        </LineScopeContext.Provider>
      ))}
    </>
  );
}

/**
 * Filters quote lines by an `fp-lines` filter value.
 * @param lines - The candidate lines in document order
 * @param filter - The line-type filter
 * @returns The matching lines in order
 */
export function filterLines(
  lines: readonly PublicQuoteLine[],
  filter: FpLinesFilter,
): PublicQuoteLine[] {
  switch (filter) {
    case "items":
      return lines.filter((line) => line.type === "ITEM");
    case "discounts":
      return lines.filter((line) => line.type === "DISCOUNT");
    case "notes":
      return lines.filter((line) => line.type === "NOTE");
    case "sections":
      return lines.filter((line) => line.type === "SECTION");
    case "all":
      return [...lines];
  }
}

/**
 * The lines belonging to a SECTION line: those following it up to the next
 * SECTION line.
 * @param lines - The quote's lines in document order
 * @param section - The section whose member lines are collected
 * @returns The section's member lines in order
 */
export function sectionMemberLines(
  lines: readonly PublicQuoteLine[],
  section: PublicQuoteSectionLine,
): PublicQuoteLine[] {
  const sectionIndex = lines.findIndex((line) => line.id === section.id);
  if (sectionIndex === -1) {
    return [];
  }
  const rest = lines.slice(sectionIndex + 1);
  const nextSectionIndex = rest.findIndex((line) => line.type === "SECTION");
  return nextSectionIndex === -1 ? rest : rest.slice(0, nextSectionIndex);
}
